"use client";

import { useState, useEffect } from "react";
import MasterLayout from "../Sections/MasterLayout";
import { useParams, useNavigate } from "react-router-dom";
import {
  server_post_data,
  get_line_by_id,
  save_update_route,
  get_route_by_primary_id,
} from "../../ServiceConnection/serviceconnection";
import Loader from "./Loader";
import SuccesModal from "../Sections/SuccesModal";
import { Plus, Trash2, MapPin, Clock, Route } from "lucide-react";

export default function AddRoute() {
  const { line_id, primary_id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [stations, setstationData] = useState([]);

  // Added primary_id to formData for editing
  const [formData, setFormData] = useState({
    primary_id: primary_id || "0",
    routeName: "",
    status: "Operational",
    startStation: "",
    startStationid: "",
    startStationFirstTrainTime: "06:00",
    startStationLastTrainTime: "23:00",
    startStationDistance: "",
    endStationid: "",
    endStation: "",
    endStationFirstTrainTime: "06:00",
    endStationLastTrainTime: "23:00",
    endStationDistance: "",
    length: "",
    open_hour: "",
    close_hour: "",
    frequency: "",
  });

  const [intermediateStations, setIntermediateStations] = useState([]);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleStationChange = (e, type) => {
    const selectedId = e.target.value;
    const selectedStation = stations.find(
      (station) => station.station_id.toString() === selectedId
    );
    if (selectedStation) {
      setFormData((prev) => ({
        ...prev,
        [`${type}`]: selectedStation.name,
        [`${type}id`]: selectedId,
      }));
    }
  };

  const addIntermediateStation = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const newStation = {
      tempId: Date.now(),
      station_id: "",
      station_name: "",
      distanceFromPrevious: "",
      firstTrainTime: formData.open_hour || "06:00",
      lastTrainTime: formData.close_hour || "23:00",
      isInterchange: false,
    };
    setIntermediateStations([...intermediateStations, newStation]);
  };

  const removeIntermediateStation = (e, tempId) => {
    e.preventDefault();
    e.stopPropagation();
    setIntermediateStations(
      intermediateStations.filter((station) => station.tempId !== tempId)
    );
  };

  const updateIntermediateStation = (tempId, field, value) => {
    setIntermediateStations(
      intermediateStations.map((station) => {
        if (station.tempId === tempId) {
          if (field === "station_id") {
            const selectedStation = stations.find(
              (s) => s.station_id.toString() === value
            );
            return {
              ...station,
              station_id: selectedStation ? selectedStation.station_id : "",
              station_name: selectedStation ? selectedStation.name : "",
            };
          }
          return { ...station, [field]: value };
        }
        return station;
      })
    );
  };

  const getAvailableStations = (currentTempId) => {
    const usedStationIds = [
      formData.startStationid,
      formData.endStationid,
      ...intermediateStations
        .filter((s) => s.tempId !== currentTempId)
        .map((s) => s.station_id.toString())
        .filter(Boolean),
    ];
    return stations.filter(
      (station) => !usedStationIds.includes(station.station_id.toString())
    );
  };

  const calculateTotalDistance = () => {
    const distances = [
      Number.parseFloat(formData.startStationDistance) || 0,
      ...intermediateStations.map(
        (station) => Number.parseFloat(station.distanceFromPrevious) || 0
      ),
      Number.parseFloat(formData.endStationDistance) || 0,
    ];
    return distances
      .reduce((total, distance) => total + distance, 0)
      .toFixed(1);
  };

  useEffect(() => {
    const totalDistance = calculateTotalDistance();
    setFormData((prev) => ({
      ...prev,
      length: `${totalDistance} km`,
    }));
  }, [
    intermediateStations,
    formData.startStationDistance,
    formData.endStationDistance,
  ]);

  const validate = () => {
    const newErrors = {};
    const {
      routeName,
      status,
      startStation,
      startStationFirstTrainTime,
      startStationLastTrainTime,
      startStationDistance,
      endStation,
      endStationFirstTrainTime,
      endStationLastTrainTime,
      endStationDistance,
      length,
      open_hour,
      close_hour,
      frequency,
    } = formData;

    if (!routeName) newErrors.routeName = "Route Name is required";
    if (!status) newErrors.status = "Status is required";
    if (!startStation) newErrors.startStation = "Start Station is required";
    if (!startStationFirstTrainTime)
      newErrors.startStationFirstTrainTime =
        "Start Station First Train Time is required";
    if (!startStationLastTrainTime)
      newErrors.startStationLastTrainTime =
        "Start Station Last Train Time is required";
    if (!startStationDistance || Number.parseFloat(startStationDistance) < 0)
      newErrors.startStationDistance = "Valid distance is required";
    if (!endStation) newErrors.endStation = "End Station is required";
    if (!endStationFirstTrainTime)
      newErrors.endStationFirstTrainTime =
        "End Station First Train Time is required";
    if (!endStationLastTrainTime)
      newErrors.endStationLastTrainTime =
        "End Station Last Train Time is required";
    if (!endStationDistance || Number.parseFloat(endStationDistance) <= 0)
      newErrors.endStationDistance = "Valid distance is required";
    if (!length) newErrors.length = "Length is required";
    if (!open_hour) newErrors.open_hour = "First train time is required";
    if (!close_hour) newErrors.close_hour = "Last train time is required";
    if (!frequency) newErrors.frequency = "Frequency is required";

    intermediateStations.forEach((station, index) => {
      if (!station.station_id) {
        newErrors[`intermediate_${station.tempId}_station`] =
          "Station selection is required";
      }
      if (
        !station.distanceFromPrevious ||
        Number.parseFloat(station.distanceFromPrevious) <= 0
      ) {
        newErrors[`intermediate_${station.tempId}_distance`] =
          "Valid distance is required";
      }
      if (!station.firstTrainTime) {
        newErrors[`intermediate_${station.tempId}_first`] =
          "First train time is required";
      }
      if (!station.lastTrainTime) {
        newErrors[`intermediate_${station.tempId}_last`] =
          "Last train time is required";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    // Fetch stations and, if editing, route details
    getline("4");
    if (primary_id) {
      getroutedetails("4");
    }
  }, [line_id, primary_id]);

  const getline = async (city_id) => {
    setIsLoading(true);
    try {
      const fd = new FormData();
      fd.append("city_id", city_id);
      fd.append("line_id", line_id);
      const response = await server_post_data(get_line_by_id, fd);
      setstationData(response.data.stations);
    } catch (error) {
      console.error("Error fetching line data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getroutedetails = async (city_id) => {
    setIsLoading(true);
    try {
      const fd = new FormData();
      fd.append("city_id", city_id);
      fd.append("line_id", line_id);
      fd.append("primary_id", primary_id);
      const response = await server_post_data(get_route_by_primary_id, fd);
      const routeData = response.data.data;

      // Populate formData with API data
      setFormData({
        primary_id: routeData.primary_id.toString(),
        routeName: routeData.route_name || "",
        status: routeData.status || "Operational",
        startStation: routeData.origin_station_name || "",
        startStationid: routeData.origin_station_id.toString() || "",
        startStationFirstTrainTime:
          routeData.station_sequence
            .find((s) => s.type === "start")
            ?.arrival_time?.slice(0, 5) || "06:00",
        startStationLastTrainTime:
          routeData.station_sequence
            .find((s) => s.type === "start")
            ?.departure_time?.slice(0, 5) || "23:00",
        startStationDistance:
          routeData.station_sequence
            .find((s) => s.type === "start")
            ?.distance_from_previous.toString() || "",
        endStation: routeData.terminate_station_name || "",
        endStationid: routeData.terminate_station_id.toString() || "",
        endStationFirstTrainTime:
          routeData.station_sequence
            .find((s) => s.type === "end")
            ?.arrival_time?.slice(0, 5) || "06:00",
        endStationLastTrainTime:
          routeData.station_sequence
            .find((s) => s.type === "end")
            ?.departure_time?.slice(0, 5) || "23:00",
        endStationDistance:
          routeData.station_sequence
            .find((s) => s.type === "end")
            ?.distance_from_previous.toString() || "",
        length: routeData.total_distance || "",
        open_hour: routeData.open_hour?.slice(0, 5) || "",
        close_hour: routeData.close_hour?.slice(0, 5) || "",
        frequency: routeData.frequency || "",
      });

      // Populate intermediateStations
      const intermediate = routeData.station_sequence
        .filter((station) => station.type === "intermediate")
        .map((station) => ({
          tempId: Date.now() + Math.random(), // Unique ID for React
          station_id: station.station_id.toString(),
          station_name: station.station_name,
          distanceFromPrevious: station.distance_from_previous.toString(),
          firstTrainTime: station.arrival_time.slice(0, 5),
          lastTrainTime: station.departure_time.slice(0, 5),
          isInterchange: station.is_interchange === 1,
        }));
      setIntermediateStations(intermediate);
    } catch (error) {
      console.error("Error fetching route details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const logStationSequence = () => {
    const stationSequence = [];
    let currentOrder = 1;

    if (formData.startStationid && formData.startStation) {
      stationSequence.push({
        station_id: formData.startStationid,
        station_name: formData.startStation,
        type: "start",
        distanceFromPrevious: formData.startStationDistance,
        firstTrainTime: formData.startStationFirstTrainTime,
        lastTrainTime: formData.startStationLastTrainTime,
        order: currentOrder,
      });
      currentOrder++;
    }

    intermediateStations.forEach((station) => {
      if (station.station_id && station.station_name) {
        stationSequence.push({
          station_id: station.station_id,
          station_name: station.station_name,
          type: "intermediate",
          distanceFromPrevious: station.distanceFromPrevious,
          firstTrainTime: station.firstTrainTime,
          lastTrainTime: station.lastTrainTime,
          isInterchange: station.isInterchange,
          order: currentOrder,
        });
        currentOrder++;
      }
    });

    if (formData.endStationid && formData.endStation) {
      stationSequence.push({
        station_id: formData.endStationid,
        station_name: formData.endStation,
        type: "end",
        distanceFromPrevious: formData.endStationDistance,
        firstTrainTime: formData.endStationFirstTrainTime,
        lastTrainTime: formData.endStationLastTrainTime,
        order: currentOrder,
      });
    }

    return stationSequence;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.routeName) {
      alert("Please enter a route name");
      return;
    }

    if (!formData.startStationid) {
      alert("Please select a start station");
      return;
    }

    if (!formData.endStationid) {
      alert("Please select an end station");
      return;
    }

    if (!validate()) {
      return;
    }

    const stationSequence = logStationSequence();

    setIsLoading(true);

    try {
      const fd = new FormData();
      fd.append("route_name", formData.routeName);
      fd.append("status", formData.status);
      fd.append("city_id", "4");
      fd.append("line_id", line_id);
      fd.append("origin_station_id", formData.startStationid);
      fd.append("origin_station_name", formData.startStation);
      fd.append("terminate_station_id", formData.endStationid);
      fd.append("terminate_station_name", formData.endStation);
      fd.append("total_distance", formData.length);
      fd.append("open_hour", formData.open_hour);
      fd.append("close_hour", formData.close_hour);
      fd.append("frequency", formData.frequency);
      fd.append("primary_id", formData.primary_id);

      if (stationSequence.length > 0) {
        fd.append("station_sequence", JSON.stringify(stationSequence));
      }

      const response = await server_post_data(save_update_route, fd);
      setShowSuccessModal(true);

      // Reset form only if adding new route
      if (!primary_id) {
        setFormData({
          primary_id: "0",
          routeName: "",
          status: "Operational",
          startStation: "",
          startStationid: "",
          startStationFirstTrainTime: "06:00",
          startStationLastTrainTime: "23:00",
          startStationDistance: "",
          endStationid: "",
          endStation: "",
          endStationFirstTrainTime: "06:00",
          endStationLastTrainTime: "23:00",
          endStationDistance: "",
          length: "",
          open_hour: "",
          close_hour: "",
          frequency: "",
        });
        setIntermediateStations([]);
      }

      setTimeout(() => {
        setShowSuccessModal(false);
        navigate(-1);
      }, 2000);
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Error saving route. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MasterLayout>
      {isLoading && <Loader />}
      <div className="max-w-6xl bg-white p-8 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center gap-3 mb-6">
          <Route className="h-6 w-6 text-blue-600" />
          <h2 className="text-2xl font-semibold">
            {primary_id ? "Edit Metro Route" : "Add Metro Route"}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Route Information */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Route Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Route Name *
                </label>
                <input
                  type="text"
                  name="routeName"
                  value={formData.routeName}
                  onChange={handleChange}
                  className={`w-full border px-3 py-2 rounded-md ${
                    errors.routeName ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="e.g., Blue Line Express"
                />
                {errors.routeName && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.routeName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Status *
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className={`w-full border px-3 py-2 rounded-md ${
                    errors.status ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="Operational">Operational</option>
                  <option value="Under Construction">Under Construction</option>
                  <option value="Planned">Planned</option>
                </select>
                {errors.status && (
                  <p className="text-sm text-red-500 mt-1">{errors.status}</p>
                )}
              </div>
            </div>
          </div>

          {/* Start Station */}
          <div className="bg-green-50 p-6 rounded-lg">
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Start Station
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Start Station *
                </label>
                <select
                  value={formData.startStationid}
                  onChange={(e) => handleStationChange(e, "startStation")}
                  className={`w-full border px-3 py-2 rounded-md ${
                    errors.startStation ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="">Select Start Station</option>
                  {stations
                    .filter(
                      (station) =>
                        station.station_id.toString() !== formData.endStationid
                    )
                    .map((station) => (
                      <option
                        key={station.station_id}
                        value={station.station_id}
                      >
                        {station.name} (ID: {station.station_id})
                      </option>
                    ))}
                </select>
                {errors.startStation && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.startStation}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Distance (km) *
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  name="startStationDistance"
                  value={formData.startStationDistance}
                  onChange={handleChange}
                  className={`w-full border px-3 py-2 rounded-md ${
                    errors.startStationDistance
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="0.0"
                />
                {errors.startStationDistance && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.startStationDistance}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  First Train Time *
                </label>
                <input
                  type="time"
                  name="startStationFirstTrainTime"
                  value={formData.startStationFirstTrainTime}
                  onChange={handleChange}
                  className={`w-full border px-3 py-2 rounded-md ${
                    errors.startStationFirstTrainTime
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                {errors.startStationFirstTrainTime && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.startStationFirstTrainTime}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Last Train Time *
                </label>
                <input
                  type="time"
                  name="startStationLastTrainTime"
                  value={formData.startStationLastTrainTime}
                  onChange={handleChange}
                  className={`w-full border px-3 py-2 rounded-md ${
                    errors.startStationLastTrainTime
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                {errors.startStationLastTrainTime && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.startStationLastTrainTime}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Intermediate Stations */}
          <div className="bg-blue-50 p-6 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium flex items-center gap-2">
                <Route className="h-5 w-5" />
                Intermediate Stations ({intermediateStations.length})
              </h3>
              <button
                type="button"
                onClick={addIntermediateStation}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add Station
              </button>
            </div>

            {intermediateStations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <MapPin className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No intermediate stations added yet.</p>
                <p className="text-sm">
                  Click "Add Station" to add stations between start and end
                  points.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {intermediateStations.map((station, index) => (
                  <div
                    key={station.tempId}
                    className="bg-white p-4 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">
                        Station {index + 1}
                        {station.station_name && (
                          <span className="text-sm text-gray-500 ml-2">
                            ({station.station_name} - ID: {station.station_id})
                          </span>
                        )}
                      </h4>
                      <button
                        type="button"
                        onClick={(e) =>
                          removeIntermediateStation(e, station.tempId)
                        }
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="lg:col-span-2">
                        <label className="block text-sm font-medium mb-1">
                          Station Name
                        </label>
                        <select
                          value={station.station_id}
                          onChange={(e) =>
                            updateIntermediateStation(
                              station.tempId,
                              "station_id",
                              e.target.value
                            )
                          }
                          className={`w-full border px-3 py-2 rounded-md text-sm ${
                            errors[`intermediate_${station.tempId}_station`]
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        >
                          <option value="">Select Station</option>
                          {getAvailableStations(station.tempId).map(
                            (availableStation) => (
                              <option
                                key={availableStation.station_id}
                                value={availableStation.station_id}
                              >
                                {availableStation.name} (ID:{" "}
                                {availableStation.station_id})
                              </option>
                            )
                          )}
                        </select>
                        {errors[`intermediate_${station.tempId}_station`] && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors[`intermediate_${station.tempId}_station`]}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Distance (km)
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          min="0.1"
                          value={station.distanceFromPrevious}
                          onChange={(e) =>
                            updateIntermediateStation(
                              station.tempId,
                              "distanceFromPrevious",
                              e.target.value
                            )
                          }
                          className={`w-full border px-3 py-2 rounded-md text-sm ${
                            errors[`intermediate_${station.tempId}_distance`]
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                          placeholder="2.5"
                        />
                        {errors[`intermediate_${station.tempId}_distance`] && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors[`intermediate_${station.tempId}_distance`]}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={station.isInterchange}
                            onChange={(e) =>
                              updateIntermediateStation(
                                station.tempId,
                                "isInterchange",
                                e.target.checked
                              )
                            }
                            className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="text-sm font-medium">
                            Interchange
                          </span>
                        </label>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          First Train Time
                        </label>
                        <input
                          type="time"
                          value={station.firstTrainTime}
                          onChange={(e) =>
                            updateIntermediateStation(
                              station.tempId,
                              "firstTrainTime",
                              e.target.value
                            )
                          }
                          className={`w-full border px-3 py-2 rounded-md text-sm ${
                            errors[`intermediate_${station.tempId}_first`]
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {errors[`intermediate_${station.tempId}_first`] && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors[`intermediate_${station.tempId}_first`]}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Last Train Time
                        </label>
                        <input
                          type="time"
                          value={station.lastTrainTime}
                          onChange={(e) =>
                            updateIntermediateStation(
                              station.tempId,
                              "lastTrainTime",
                              e.target.value
                            )
                          }
                          className={`w-full border px-3 py-2 rounded-md text-sm ${
                            errors[`intermediate_${station.tempId}_last`]
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {errors[`intermediate_${station.tempId}_last`] && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors[`intermediate_${station.tempId}_last`]}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* End Station */}
          <div className="bg-red-50 p-6 rounded-lg">
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              End Station
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">
                  End Station *
                </label>
                <select
                  value={formData.endStationid}
                  onChange={(e) => handleStationChange(e, "endStation")}
                  className={`w-full border px-3 py-2 rounded-md ${
                    errors.endStation ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="">Select End Station</option>
                  {stations
                    .filter(
                      (station) =>
                        station.station_id.toString() !==
                        formData.startStationid
                    )
                    .map((station) => (
                      <option
                        key={station.station_id}
                        value={station.station_id}
                      >
                        {station.name} (ID: {station.station_id})
                      </option>
                    ))}
                </select>
                {errors.endStation && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.endStation}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Distance (km) *
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  name="endStationDistance"
                  value={formData.endStationDistance}
                  onChange={handleChange}
                  className={`w-full border px-3 py-2 rounded-md ${
                    errors.endStationDistance
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="2.5"
                />
                {errors.endStationDistance && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.endStationDistance}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  First Train Time *
                </label>
                <input
                  type="time"
                  name="endStationFirstTrainTime"
                  value={formData.endStationFirstTrainTime}
                  onChange={handleChange}
                  className={`w-full border px-3 py-2 rounded-md ${
                    errors.endStationFirstTrainTime
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                {errors.endStationFirstTrainTime && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.endStationFirstTrainTime}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Last Train Time *
                </label>
                <input
                  type="time"
                  name="endStationLastTrainTime"
                  value={formData.endStationLastTrainTime}
                  onChange={handleChange}
                  className={`w-full border px-3 py-2 rounded-md ${
                    errors.endStationLastTrainTime
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                {errors.endStationLastTrainTime && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.endStationLastTrainTime}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Operating Details */}
          <div className="bg-green-50 p-6 rounded-lg">
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Operating Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Total Length *
                </label>
                <input
                  type="text"
                  name="length"
                  placeholder="e.g., 15.2 km"
                  value={formData.length}
                  onChange={handleChange}
                  className={`w-full border px-3 py-2 rounded-md ${
                    errors.length ? "border-red-500" : "border-gray-300"
                  }`}
                  readOnly
                />
                {errors.length && (
                  <p className="text-sm text-red-500 mt-1">{errors.length}</p>
                )}
                <p className="text-xs text-blue-600 mt-1">
                  Auto-calculated from stations
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  First Train Time *
                </label>
                <input
                  type="time"
                  name="open_hour"
                  value={formData.open_hour}
                  onChange={handleChange}
                  className={`w-full border px-3 py-2 rounded-md ${
                    errors.open_hour ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.open_hour && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.open_hour}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Last Train Time *
                </label>
                <input
                  type="time"
                  name="close_hour"
                  value={formData.close_hour}
                  onChange={handleChange}
                  className={`w-full border px-3 py-2 rounded-md ${
                    errors.close_hour ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.close_hour && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.close_hour}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Frequency *
                </label>
                <input
                  type="text"
                  name="frequency"
                  placeholder="e.g., 5 min"
                  value={formData.frequency}
                  onChange={handleChange}
                  className={`w-full border px-3 py-2 rounded-md ${
                    errors.frequency ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.frequency && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.frequency}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Route Summary */}
          {(formData.startStation ||
            formData.endStation ||
            intermediateStations.length > 0) && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-medium mb-4">Route Summary</h3>
              <div className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
                {formData.startStation && (
                  <>
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded">
                      {formData.startStation} (ID: {formData.startStationid})
                    </span>
                    {(intermediateStations.length > 0 ||
                      formData.endStation) && <span>→</span>}
                  </>
                )}

                {intermediateStations.map((station, index) => (
                  <div key={station.tempId} className="flex items-center gap-2">
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        station.isInterchange
                          ? "bg-orange-100 text-orange-800"
                          : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      {station.station_name || `Station ${index + 1}`}
                      {station.station_id && ` (ID: ${station.station_id})`}
                      {station.isInterchange && " (Interchange)"}
                    </span>
                    {(index < intermediateStations.length - 1 ||
                      formData.endStation) && <span>→</span>}
                  </div>
                ))}

                {formData.endStation && (
                  <span className="px-2 py-1 bg-red-100 text-red-800 rounded">
                    {formData.endStation} (ID: {formData.endStationid})
                  </span>
                )}
              </div>

              {(formData.startStationDistance ||
                intermediateStations.length > 0 ||
                formData.endStationDistance) && (
                <div className="mt-3 text-sm text-gray-600">
                  <strong>Total Calculated Distance:</strong>{" "}
                  {calculateTotalDistance()} km
                </div>
              )}
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end pt-6 gap-3 border-t">
            <button
              type="button"
              className="px-6 py-2 rounded-md btn-secoundary"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center justify-center gap-2 bg-[var(--primary-color)] text-white px-6 py-2 rounded-md hover:bg-[var(--primary-hover)] transition duration-200 disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  {primary_id ? "Update Route" : "Add Route"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <SuccesModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={
          primary_id
            ? "Route Updated Successfully!"
            : "Route Added Successfully!"
        }
        subtitle={
          primary_id
            ? "Your route details have been updated in the system."
            : "Your route details have been saved and added to the system."
        }
        autoClose={false}
      />
    </MasterLayout>
  );
}
